import { ServiceTypes } from 'constants/ServiceTypes';
import { PitchingProgressState } from 'enums/PitchingProgressState';
import { observer } from 'mobx-react-lite';
import React from 'react';
import Lottie, { Options } from 'react-lottie';
import { useInjection } from 'services/ioc/ioc.react';
import { FieldCalibrationStore } from 'stores/FieldCalibrationStore';
import step4 from './images/animations/step4.json';
import { ReactComponent as CheckmarkIcon } from './images/checkmarkIcon.svg';
import { ReactComponent as CloseIcon } from './images/closeIcon.svg';
import { ReactComponent as SmallCheckmarkIcon } from './images/smallCheckmarkIcon.svg';
import { Background, Description, Header, HeaderLabel, Icon, InfoList, PitchResultContainer } from './styles/PitchingProgressImageStyles';

const PitchingProgressImage: React.FC = observer(() => {
  const fieldCalibrationStore = useInjection<FieldCalibrationStore>(ServiceTypes.FieldCalibrationStore);

  const defaultOptions: Options = {
    loop: true,
    autoplay: true,
    animationData: step4,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  const getHeaderLabel = (): JSX.Element | null => {
    switch (fieldCalibrationStore.pitchingProgressState) {
      case PitchingProgressState.StartPitching:
        return <HeaderLabel>Start pitching</HeaderLabel>;
      case PitchingProgressState.PitchRegistered:
        return <HeaderLabel>Registered</HeaderLabel>;
      case PitchingProgressState.TryAgain:
        return <HeaderLabel>Try again</HeaderLabel>;
      case PitchingProgressState.WaitingForAPitch:
        return <HeaderLabel>Waiting for a pitch...</HeaderLabel>;
      default:
        return null;
    }
  };

  const getIcon = (): JSX.Element | null => {
    switch (fieldCalibrationStore.pitchingProgressState) {
      case PitchingProgressState.PitchRegistered:
        return (
          <Icon>
            <SmallCheckmarkIcon />
          </Icon>
        );
      case PitchingProgressState.TryAgain:
        return (
          <Icon>
            <CloseIcon />
          </Icon>
        );
      default:
        return null;
    }
  };

  const getBreaksInfo = () => {
    if (fieldCalibrationStore.breaksTracked) {
      return <span>Breaks tracked</span>;
    }

    return (
      <span>
        Breaks <u>not</u> tracked
      </span>
    );
  };

  const getInfoList = (): JSX.Element | null => {
    switch (fieldCalibrationStore.pitchingProgressState) {
      case PitchingProgressState.StartPitching:
      case PitchingProgressState.WaitingForAPitch:
        return (
          <InfoList>
            <li>Minimum speed: 50 mph</li>
            <li>Direction: landing area</li>
          </InfoList>
        );
      case PitchingProgressState.TryAgain:
        return (
          <InfoList>
            <li>Throw to the landing area</li>
            <li>Ensure home plate is clean and visible </li>
          </InfoList>
        );
      case PitchingProgressState.PitchRegistered:
        if (fieldCalibrationStore.hitHP) {
          return (
            <InfoList>
              <li>On home plate</li>
              <li>{getBreaksInfo()}</li>
            </InfoList>
          );
        }
        return (
          <InfoList>
            <li>In landing area</li>
            <li>{getBreaksInfo()}</li>
          </InfoList>
        );
      default:
        return null;
    }
  };

  const getEnoughtPitchesContent = () => {
    return (
      <>
        <Icon>
          <CheckmarkIcon />
        </Icon>
        <HeaderLabel>Enough pitches registered </HeaderLabel>
        <Description>You can finish the data capture</Description>
      </>
    );
  };

  return (
    <Background>
      <Lottie options={defaultOptions} isClickToPauseDisabled={true} />
      <PitchResultContainer visible={fieldCalibrationStore.pitchingProgressState !== undefined}>
        {fieldCalibrationStore.pitchingProgressState === PitchingProgressState.EnoughPitches && getEnoughtPitchesContent()}
        {fieldCalibrationStore.pitchingProgressState !== PitchingProgressState.EnoughPitches && (
          <>
            <Header>
              {getIcon()}
              {getHeaderLabel()}
            </Header>
            {getInfoList()}
          </>
        )}
      </PitchResultContainer>
    </Background>
  );
});

export default PitchingProgressImage;
